import React from 'react';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { TrackingEvent } from '../../services/shippingService';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Divider } from '../../components/ui/Divider';
import { Icon } from '../../components/ui/Icon';

export type TimelineStatusInfo = {
  step: number;
  iconName: string;
  tone: 'ok' | 'info' | 'warn' | 'err' | 'default';
  label: string;
  isSubStatus: boolean;
  parentStatus?: string;
};

export type TimelineGroup = {
  status: string;
  displayStatus: string;
  events: TrackingEvent[];
  subGroups?: Array<{ status: string; events: TrackingEvent[]; statusInfo: TimelineStatusInfo }>;
  isLatest: boolean;
  statusInfo: TimelineStatusInfo;
};

const formatDateTime = (dateString: string) => {
  try {
    const d = parseISO(dateString);
    return {
      date: format(d, 'MMM d, yyyy'),
      time: format(d, 'h:mm a'),
      relative: formatDistanceToNow(d, { addSuffix: true }),
    };
  } catch {
    return { date: 'Invalid date', time: '', relative: '' };
  }
};

export const TrackEventsTimeline: React.FC<{
  groupedEvents: TimelineGroup[];
  totalEvents: number;
  onRefresh: () => void;
}> = ({ groupedEvents, totalEvents, onRefresh }) => (
  <Card>
    <div className="card-header">
      <div className="card-title">
        Shipment events
        <span className="muted" style={{ fontWeight: 400, marginInlineStart: 8 }}>
          · {totalEvents} updates
        </span>
      </div>
      <Button size="xs" variant="ghost" onClick={onRefresh}>
        <Icon name="refresh" size={12} />
        <span>Refresh</span>
      </Button>
    </div>
    <div style={{ padding: 20 }}>
      {groupedEvents.length === 0 && (
        <div className="muted" style={{ padding: 24, textAlign: 'center' }}>
          No events recorded yet for this shipment.
        </div>
      )}
      {groupedEvents.map((group, gi) => (
        <div key={`${group.status}-${gi}`}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Badge tone={group.statusInfo.tone} dot>
              {group.displayStatus}
            </Badge>
            {group.isLatest && (
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-blue)' }}>
                LATEST
              </span>
            )}
          </div>

          {group.events.map((e, ei) => {
            const f = formatDateTime(e.timestamp);
            const isVeryFirst = ei === 0 && gi === 0;
            return (
              <div
                key={`${gi}-${ei}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '92px 18px 1fr',
                  gap: 14,
                  padding: '10px 0',
                  borderBottom: '1px solid var(--line)',
                }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{f.date}</div>
                  <div className="mono muted" style={{ fontSize: 12 }}>
                    {f.time}
                  </div>
                </div>
                <div style={{ position: 'relative' }}>
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 99,
                      marginTop: 6,
                      background: isVeryFirst ? 'var(--brand-blue)' : 'var(--line-strong)',
                      boxShadow: isVeryFirst ? '0 0 0 3px rgba(43,85,255,0.18)' : 'none',
                    }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: isVeryFirst ? 600 : 500 }}>
                    {e.description || group.displayStatus}
                  </div>
                  {e.location && (
                    <div className="muted" style={{ fontSize: 13 }}>
                      {e.location}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {group.subGroups?.map((sub, si) => (
            <div
              key={`sub-${gi}-${si}`}
              style={{
                marginInlineStart: 18,
                paddingInlineStart: 14,
                borderInlineStart: '1px dashed var(--line)',
              }}
            >
              <div style={{ marginTop: 8, marginBottom: 4 }}>
                <Badge tone={sub.statusInfo.tone}>{sub.statusInfo.label}</Badge>
              </div>
              {sub.events.map((e, ei) => {
                const f = formatDateTime(e.timestamp);
                return (
                  <div
                    key={`subevent-${gi}-${si}-${ei}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '92px 1fr',
                      gap: 14,
                      padding: '6px 0',
                      fontSize: 13,
                    }}
                  >
                    <div className="muted">
                      {f.date} · <span className="mono">{f.time}</span>
                    </div>
                    <div>
                      {e.description || sub.statusInfo.label}
                      {e.location && (
                        <div className="muted" style={{ fontSize: 12 }}>
                          {e.location}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {gi < groupedEvents.length - 1 && <Divider style={{ margin: '14px 0' }} />}
        </div>
      ))}
    </div>
  </Card>
);

export default TrackEventsTimeline;
